import React, { useState } from "react";
import { View, Text, Image, Pressable, ActivityIndicator, StyleSheet } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { toRegistryCandidate } from "../../../utils/affiliateUrl";
import RegistrySetupModal, { REGISTRY_SETUP_COLORS } from "./RegistrySetupModal";
import RegistrySetupButton from "./RegistrySetupButton";
import useStarterProducts from "./useStarterProducts";
import { SPOTLIGHT_TARGETS } from "../../../Widgets/SpotlightTutorial/spotlightTargets";

const ADD_FAILED_MESSAGE = "We couldn't add your items. Please try again.";

const StarterProductRow = ({ product, isSelected, onToggle }) => (
	<Pressable
		style={[styles.row, isSelected && styles.rowSelected]}
		onPress={() => onToggle(product.id)}
		accessibilityRole="checkbox"
		accessibilityState={{ checked: isSelected }}
	>
		<View style={[styles.checkbox, isSelected && styles.checkboxChecked]}>
			{isSelected && <Ionicons name="checkmark" size={16} color="#fff" />}
		</View>
		<Image source={{ uri: product.image }} style={styles.productImage} resizeMode="contain" />
		<View style={styles.productDetails}>
			<Text style={styles.productName} numberOfLines={2}>
				{product.name}
			</Text>
			{product.price && <Text style={styles.productPrice}>{product.price}</Text>}
		</View>
	</Pressable>
);

const StarterStatusMessage = ({ status, onRetry }) => {
	if (status === "loading") return <ActivityIndicator style={styles.status} size="large" color={REGISTRY_SETUP_COLORS.green} />;
	return (
		<View style={styles.status}>
			<Text style={styles.statusText}>We couldn't load this starter list.</Text>
			<RegistrySetupButton label="Try Again" variant="secondary" onPress={onRetry} />
		</View>
	);
};

const RegistryStarterItems = ({ template, onBack, onAddItems, onClose }) => {
	const { status, products, retry } = useStarterProducts(template);
	const [uncheckedIds, setUncheckedIds] = useState(new Set());
	const [isAdding, setIsAdding] = useState(false);
	const [addError, setAddError] = useState("");

	const selectedProducts = products.filter((product) => !uncheckedIds.has(product.id));

	const toggleProduct = (productId) => {
		setUncheckedIds((previous) => {
			const next = new Set(previous);
			if (!next.delete(productId)) next.add(productId);
			return next;
		});
	};

	const addSelectedItems = async () => {
		if (isAdding || selectedProducts.length === 0) return;
		setIsAdding(true);
		setAddError("");
		let wasAdded = false;
		try {
			wasAdded = await onAddItems(selectedProducts.map(toRegistryCandidate));
		} catch (error) {
			console.error("Failed to add starter items:", error);
		}
		setIsAdding(false);
		if (!wasAdded) setAddError(ADD_FAILED_MESSAGE);
	};

	const canAdd = selectedProducts.length > 0 && !isAdding;
	const footer = (
		<View style={styles.footer}>
			{addError ? <Text style={styles.errorText}>{addError}</Text> : null}
			{selectedProducts.length === 0 && status === "ready" && <Text style={styles.hintText}>Select at least one item to continue.</Text>}
			<RegistrySetupButton label={isAdding ? "Adding..." : "Add Selected Items to Registry"} onPress={addSelectedItems} disabled={!canAdd} />
		</View>
	);

	return (
		<RegistrySetupModal
			title={template.title}
			subtitle="We've preselected some useful starter items. Uncheck anything you don't want."
			onClose={onClose}
			footer={footer}
			targetId={SPOTLIGHT_TARGETS.REGISTRY_STARTER_ITEMS}
		>
			<View style={styles.toolbar}>
				<Pressable style={styles.backButton} onPress={onBack} disabled={isAdding} accessibilityRole="button">
					<Ionicons name="arrow-back" size={18} color={REGISTRY_SETUP_COLORS.green} />
					<Text style={styles.backLabel}>Back</Text>
				</Pressable>
				{status === "ready" && (
					<Text style={styles.selectionCount}>
						{selectedProducts.length} of {products.length} selected
					</Text>
				)}
			</View>
			{status === "ready" ? (
				products.map((product) => <StarterProductRow key={product.id} product={product} isSelected={!uncheckedIds.has(product.id)} onToggle={toggleProduct} />)
			) : (
				<StarterStatusMessage status={status} onRetry={retry} />
			)}
		</RegistrySetupModal>
	);
};

const styles = StyleSheet.create({
	toolbar: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		paddingBottom: 8,
	},
	backButton: {
		flexDirection: "row",
		alignItems: "center",
		gap: 6,
		paddingVertical: 8,
		paddingRight: 12,
	},
	backLabel: {
		fontSize: 15,
		fontWeight: "600",
		color: REGISTRY_SETUP_COLORS.green,
	},
	selectionCount: {
		fontSize: 14,
		color: REGISTRY_SETUP_COLORS.mutedText,
	},
	row: {
		flexDirection: "row",
		alignItems: "center",
		gap: 12,
		padding: 10,
		marginBottom: 8,
		borderRadius: 12,
		borderWidth: 1,
		borderColor: REGISTRY_SETUP_COLORS.border,
		backgroundColor: "rgba(255, 255, 255, 0.5)",
	},
	rowSelected: {
		borderColor: "rgba(76, 149, 108, 0.6)",
		backgroundColor: "rgba(255, 255, 255, 0.9)",
	},
	checkbox: {
		width: 24,
		height: 24,
		borderRadius: 6,
		borderWidth: 2,
		borderColor: REGISTRY_SETUP_COLORS.green,
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "#fff",
	},
	checkboxChecked: {
		backgroundColor: REGISTRY_SETUP_COLORS.green,
	},
	productImage: {
		width: 56,
		height: 56,
		borderRadius: 8,
		backgroundColor: "#fff",
	},
	productDetails: {
		flex: 1,
		gap: 2,
	},
	productName: {
		fontSize: 14,
		fontWeight: "500",
		color: REGISTRY_SETUP_COLORS.text,
	},
	productPrice: {
		fontSize: 13,
		color: REGISTRY_SETUP_COLORS.mutedText,
	},
	status: {
		alignItems: "center",
		gap: 12,
		paddingVertical: 32,
	},
	statusText: {
		fontSize: 15,
		color: REGISTRY_SETUP_COLORS.mutedText,
	},
	footer: {
		gap: 8,
	},
	errorText: {
		fontSize: 14,
		color: "#dc2626",
		textAlign: "center",
	},
	hintText: {
		fontSize: 14,
		color: REGISTRY_SETUP_COLORS.mutedText,
		textAlign: "center",
	},
});

export default RegistryStarterItems;
